import React, { useCallback, useEffect, useMemo, useState } from "react";
import debounce from "lodash.debounce";
import api from "../../../Services/api";
import Swal from "sweetalert2";
import { FiSearch, FiX, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { LuGitCompareArrows } from "react-icons/lu";
import "./associacaoGrupoModal.css";

const LIMIT = 12;

const AssociacaoGrupoModal = ({ secaoId, onClose, onAssociarSuccess }) => {
  const [grupos, setGrupos] = useState([]);
  const [secao, setSecao] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const [filter, setFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");
  const [page, setPage] = useState(1);

  const handleDebounce = useCallback(
    debounce((value) => {
      setDebouncedFilter(value);
      setPage(1);
    }, 350),
    []
  );

  const onChangeFiltro = (e) => {
    const value = e.target.value;
    setFilter(value);
    handleDebounce(value);
  };

  useEffect(() => {
    return () => handleDebounce.cancel();
  }, [handleDebounce]);

  useEffect(() => {
    if (!secaoId) return;
    const fetchSecao = async () => {
      try {
        const response = await api.get(`/chksecao/${secaoId}`);
        setSecao(response.data);
      } catch (error) {
        console.error("Erro ao carregar seção", error);
      }
    };
    fetchSecao();
  }, [secaoId]);

  useEffect(() => {
    if (!secaoId) return;
    const fetchGrupos = async () => {
      try {
        setLoading(true);
        const [todosRes, aplicadosRes] = await Promise.all([
          api.get("/chkgrupoquestoes", {
            params: { page: 1, limit: 9999, questaoModelo: 1, situacao: 1, filter: debouncedFilter },
          }),
          api.get("/chkgrupoquestaosecoes", {
            params: { page: 1, limit: 9999, IDSecao: secaoId },
          }),
        ]);

        const idsAplicados = new Set(
          (aplicadosRes.data?.data || []).map((item) => Number(item.IDGrupoQuestao))
        );

        const elegiveis = (todosRes.data?.data || []).filter(
          (g) => !idsAplicados.has(Number(g.IDGrupoQuestao))
        );
        setGrupos(elegiveis);
      } catch (error) {
        console.error("Erro ao carregar grupos para associação", error);
        Swal.fire("Erro", "Não foi possível carregar os grupos.", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchGrupos();
  }, [secaoId, debouncedFilter]);

  const totalPages = Math.max(1, Math.ceil(grupos.length / LIMIT));

  const gruposPaginados = useMemo(() => {
    const inicio = (page - 1) * LIMIT;
    return grupos.slice(inicio, inicio + LIMIT);
  }, [grupos, page]);

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const associar = async () => {
    if (!selected) return;
    try {
      Swal.fire({
        title: "Associando...",
        text: "Aguarde",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      await api.post("/chkgrupoquestaosecao", {
        IDGrupoQuestao: selected,
        IDSecao: Number(secaoId),
      });

      Swal.close();
      await Swal.fire("Sucesso", "Grupo associado com sucesso.", "success");
      onAssociarSuccess?.();
      onClose?.();
    } catch (error) {
      console.error("Erro ao associar grupo", error);
      Swal.fire(
        "Erro",
        error?.response?.data?.errors?.default || "Não foi possível associar o grupo.",
        "error"
      );
    }
  };

  return (
    <div className="associacaoGrupo-overlay">
      <div className="associacaoGrupo-modal">
        <div className="modal-header">
          <h3 className="associacaoGrupo-title">
            <LuGitCompareArrows />
            Associar grupo à seção
          </h3>
          <button className="close-btn" onClick={onClose}>
            <FiX />
          </button>
        </div>

        {secao && (
          <div className="secaoTitle-field">
            <label>Seção:</label>
            <input type="text" value={secao.descricao} disabled />
          </div>
        )}

        <div className="modal-search">
          <FiSearch className="icon" />
          <input
            type="text"
            placeholder="Buscar grupo modelo..."
            value={filter}
            onChange={onChangeFiltro}
          />
        </div>

        <div className="lista-grupos">
          {loading && <p className="nenhum">Carregando...</p>}
          {!loading && gruposPaginados.length === 0 && (
            <p className="nenhum">Nenhum grupo modelo disponível para associação.</p>
          )}
          {!loading &&
            gruposPaginados.map((grupo) => (
              <div
                key={grupo.IDGrupoQuestao}
                className={`grupo-card ${selected === grupo.IDGrupoQuestao ? "selected" : ""}`}
                onClick={() => setSelected(grupo.IDGrupoQuestao)}
              >
                <div className="grupo-titulo">{grupo.descricao}</div>
              </div>
            ))}
        </div>

        <div className="pagination">
          <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            <FiChevronLeft size={18} style={{ marginRight: 4 }} />
          </button>
          <span>
            Página {page} de {totalPages}
          </span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            <FiChevronRight size={18} style={{ marginLeft: 4 }} />
          </button>
        </div>

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>
            Cancelar
          </button>
          <button className="confirm-btn" disabled={!selected} onClick={associar}>
            Associar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssociacaoGrupoModal;
